import React from 'react';
import { Flame, Zap, Snowflake, Sparkles, HelpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Phase 27.37.S — HOT / WARM / COLD pill for a lead.
 * Reads from `lead.ai_score` (persisted by the scoring engine).
 *
 * Props:
 *   score            — { score, confidence, reason, factors[], engine }
 *   size             — "sm" (list) | "md" (detail header)
 *   showLabel        — whether to render the text next to the icon
 */
const SCORE_META = {
  hot:  { label: 'HOT',  icon: Flame,     class: 'bg-rose-100 text-rose-800 border-rose-300' },
  warm: { label: 'WARM', icon: Zap,       class: 'bg-amber-100 text-amber-800 border-amber-300' },
  cold: { label: 'COLD', icon: Snowflake, class: 'bg-slate-100 text-slate-600 border-slate-300' },
};

export default function LeadScorePill({ score, size = 'sm', showLabel = true }) {
  if (!score || !score.score) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] border-dashed border-slate-300 text-slate-400"
        data-testid="lead-score-none"
        title="No AI score yet"
      >
        <Sparkles size={10} className="mr-1" /> {showLabel ? 'Not scored' : ''}
      </Badge>
    );
  }
  const meta = SCORE_META[score.score] || SCORE_META.cold;
  const Icon = meta.icon;
  const dot = size === 'sm' ? 10 : 12;
  const pillClass = size === 'sm'
    ? `text-[10px] ${meta.class}`
    : `text-xs px-2 py-0.5 ${meta.class}`;

  const tooltipBody = (
    <div className="space-y-1 max-w-xs">
      <div className="text-xs font-semibold flex items-center gap-1">
        <Icon size={12} /> {meta.label}
        {score.confidence != null && (
          <span className="opacity-70">· {score.confidence}% conf.</span>
        )}
      </div>
      {score.reason && <div className="text-[11px] text-slate-100">{score.reason}</div>}
      {Array.isArray(score.factors) && score.factors.length > 0 && (
        <ul className="text-[10.5px] text-slate-200 space-y-0.5 pt-1">
          {score.factors.map((f, i) => <li key={i}>· {f}</li>)}
        </ul>
      )}
      {score.engine && (
        <div className="text-[9.5px] text-slate-400 pt-1 italic">
          Scored by {score.engine === 'claude' ? 'Claude Sonnet 5' : 'rules'}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${pillClass} cursor-help`} data-testid={`lead-score-pill-${score.score}`}>
            <Icon size={dot} className="mr-1" />
            {showLabel && meta.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-slate-900 text-slate-50 border-slate-700">
          {tooltipBody}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const LEAD_SCORE_HELP = HelpCircle; // re-export for parents wanting the ? icon
